/**
 * chotot.com theme
 * @author Thanh Tran
 */

/**
 * theme.breakpoints
 *
 * When used with `sx` helper, 0 index means for all viewports (mobile first):
 *
 * ```
 * sx({
 *   // applies font size small to all viewport widths,
 *   // font size medium above the first breakpoint,
 *   // font size large above the next breakpoint
 *   fontSize: [ 'small', 'medium', 'large' ]
 * })
 * ```
 *
 * @type {{
 *   0: '576px'
 *   1: '768px'
 *   2: '992px'
 *   3: '1200px'
 *   4: '1400px'
 *   sm: '576px'
 *   md: '768px'
 *   lg: '992px'
 *   xl: '1200px'
 *   xxl: '1400px'
 * }}
 */
// @ts-ignore breakpoints must be array, but we want to use sm, md keys for easier reference
const breakpoints = ['576px', '768px', '992px', '1200px', '1400px'];
// index in sx:         1,       2,       3,       4,        5  (not a mistake, 0 is smallest viewport)

// @ts-ignore
[breakpoints.sm, breakpoints.md, breakpoints.lg, breakpoints.xl, breakpoints.xxl] = breakpoints;

/**
 * theme.mediaQueries
 *
 * Example:
 * ```
 * @media ${theme.mediaQueries.tablet} {
 *   font-size: ${theme.fontSize.small}
 * }
 * ```
 */
const mediaQueries = {
  /**
   * Special media query for occational iPhone 5 (320px) edge casess
   * @type {`screen and (max-width: 320px)` | string}
   */
  ip5: 'screen and (max-width: 320px)',
  /**
   * @type {`screen and (min-width: {breakpoints.md})` | string}
   */
  tablet: `screen and (min-width: ${breakpoints.md})`,
  /**
   * @type {`screen and (min-width: {breakpoints.lg})` | string}
   */
  desktop: `screen and (min-width: ${breakpoints.lg})`,
};

/**
  * Note: We use tuple to define string literal type in each element.
  * This will help to expose the value of the theme key with it's actual value.
  * The only caveat is that we have to duplicate the array into this JSDoc at-type
  *
  * @type {[
    '0px',
    '2px',
    '4px',
    '8px',
    '12px',
    '16px',
    '24px',
    '32px',
    '48px',
    '64px',
    '96px',
    '128px',
  ]}
  */
const spaceScale = [
  '0px', // 0
  '2px', // 1
  '4px', // 2
  '8px', // 3
  '12px', // 4
  '16px', // 5
  '24px', // 6
  '32px', // 7
  '48px', // 8
  '64px', // 9
  '96px', // 10
  '128px', // 11
];

/**
 * theme.space
 *
 * margin, margin-top, margin-right, margin-bottom, margin-left, padding, padding-top, padding-right,
 * padding-bottom, padding-left, grid-gap, grid-column-gap, grid-row-gap
 */
const space = {
  // ...spaceScale,
  none: spaceScale[0],
  xxxs: spaceScale[1],
  xxs: spaceScale[2],
  xs: spaceScale[3],
  sm: spaceScale[4],
  md: spaceScale[5],
  lg: spaceScale[6],
  xl: spaceScale[7],
  xxl: spaceScale[8],
  xxxl: spaceScale[9],
  xxxxl: spaceScale[10],
  xxxxxl: spaceScale[11],
};

/**
  * @type {[
    '12px',
    '16px',
    '20px',
    '24px',
    '28px',
    '32px',
    '48px',
    '64px',
    '80px',
    '96px',
    '128px',
    '236px',
    '352px',
    '480px',
    '636px',
    '864px',
  ]}
  */
const sizesScale = [
  '12px', // 0
  '16px', // 1
  '20px', // 2
  '24px', // 3
  '28px', // 4
  '32px', // 5
  '48px', // 6
  '64px', // 7
  '80px', // 8
  '96px', // 9
  '128px', // 10
  '236px', // 11
  '352px', // 12
  '480px', // 13
  '636px', // 14
  '864px', // 15
];

/**
  * @type {{
    vW: '100vw',
      vH: '100vh',
      quarter: '25%',
      third: '33.33%',
      half: '50%',
      twoThird: '66.66%',
      threeQuarter: '75%',
      full: '100%',
  }}
  */
const sizePct = {
  vW: '100vw',
  vH: '100vh',
  quarter: '25%',
  third: '33.33%',
  half: '50%',
  twoThird: '66.66%',
  threeQuarter: '75%',
  full: '100%',
};

/**
 * theme.sizes
 *
 * width, height, min-width, max-width, min-height, max-height
 */
const sizes = {
  // ...sizesScale,
  ...sizePct,
  xxxs: sizesScale[0],
  xxs: sizesScale[1],
  xs: sizesScale[2],
  sm: sizesScale[3],
  md: sizesScale[4],
  lg: sizesScale[5],
  xl: sizesScale[6],
  xxl: sizesScale[7],
  xxxl: sizesScale[8],
  xxxxl: sizesScale[9],
  xxxxxl: sizesScale[10],
  buttonMaxWidth: sizesScale[12],
  $sm: sizesScale[11],
  $md: sizesScale[13],
  $lg: sizesScale[14],
  $xl: sizesScale[15],
};

/**
 * theme.fonts
 *
 * font-family
 */
const fonts = /** @type {const} */ ({
  body: 'Helvetica, Arial, Roboto, sans-serif',
  heading: 'Helvetica, Arial, Roboto, sans-serif',
  monospace: 'SFMono-Regular, Consolas, Liberation Mono, Menlo, monospace',

  // Allow design system consumers to access font settings but control how and where they load the font.
  urls: {
    body: 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,400;0,700;1,400&display=swap',
  },
});

/**
 * Map px values to rem values with assumption root's font size of 16px
 * @template {number} T
 * @param {T} v
 * @return {`${T}px`}
 */
// @ts-ignore we only need the @return to match with tuple below
const rem = (v) => `${v / 16}rem`;

/**
 * Note: because we convert pixel to rem, we need an augmented tuple type
 * without the rem() function
 */

const fszScale = /** @type {const} */ ([
  rem(10), // 0
  rem(12), // 1
  rem(14), // 2
  rem(16), // 3
  rem(18), // 4
  rem(20), // 5
  rem(24), // 6
  rem(32), // 7
]);

/**
 * theme.fontSizes
 *
 * font-size
 */
const fontSizes = /** @type {const} */ ({
  // ...fszScale,
  h1: fszScale[7],
  h2: fszScale[6],
  h3: fszScale[5],
  h4: fszScale[4],
  h5: fszScale[3],
  h6: fszScale[2],
  subtitle: fszScale[1],
  body: fszScale[2],
  xxs: fszScale[0],
  xs: fszScale[1],
  sm: fszScale[2],
  md: fszScale[3],
  lg: fszScale[4],
  xl: fszScale[5],
  xxl: fszScale[6],
  xxxl: fszScale[7],
});

const lh = /** @type {const} */ ([
  1.25, // 0
  1.5, // 1
]);

/**
 * theme.lineHeights
 *
 * line-height
 */
const lineHeights = /** @type {const} */ ({
  // new semantic
  heading: lh[0],
  paragraph: lh[1],
  // legacy
  h1: lh[0],
  h2: lh[0],
  h3: lh[0],
  h4: lh[0],
  h5: lh[0],
  h6: lh[0],
  subtitle: lh[0],
  body: lh[1],
  xs: lh[1],
  xxs: lh[1],
  sm: lh[1],
  md: lh[1],
  lg: lh[1],
  xl: lh[1],
});

/**
 * theme.fontWeights
 *
 * font-weight
 */
const fontWeights = /** @type {const} */ ({
  body: 400,
  heading: 700,
  normal: 400,
  bold: 700,
});

const grays = /** @type {const} */ ({
  gray50: '#F4F4F4',
  gray75: '#E8E8E8',
  gray100: '#DADADA',
  gray200: '#C0C0C0',
  gray300: '#A7A7A7',
  gray400: '#8C8C8C',
  gray500: '#737373',
  gray600: '#595959',
  gray700: '#404040',
  gray800: '#2E2E2E',
  gray900: '#222222',
});

const legacyGrays = /** @type {const} */ ({
  black: grays.gray900,
  darkgray1: '',
  darkgray2: '',
  darkgray3: '#777777',
  darkgray4: '',
  gray1: grays.gray400,
  gray2: grays.gray200,
  gray3: grays.gray75,
  gray4: grays.gray50,
  gray5: grays.gray50,
  white: '#FFFFFF',
});

const reds = /** @type {const} */ ({
  red50: '#FFF0F2',
  red75: '#FFE5EA',
  red100: '#FCCFD7',
  red200: '#F5ADB9',
  red300: '#FC8296',
  red400: '#FF4766',
  red500: '#E5193B',
  red600: '#B01730',
  red700: '#7F1022',
  red800: '#650B1A',
  red900: '#4B0611',
});

const legacyReds = /** @type {const} */ ({
  red1: reds.red700,
  red2: reds.red600,
  red3: reds.red500,
  red4: reds.red75,
  red5: reds.red50,
});

const oranges = /** @type {const} */ ({
  orange50: '#FFF4E0',
  orange75: '#FFE9C2',
  orange100: '#FFD494',
  orange200: '#FFB057',
  orange300: '#FF8800',
  orange400: '#E56700',
  orange500: '#C74C00',
  orange600: '#9F3504',
  orange700: '#752506',
  orange800: '#5E1A03',
  orange900: '#451608',
});

const legacyOranges = {
  orange1: oranges.orange400,
  orange2: oranges.orange300,
  orange3: oranges.orange200,
  orange4: oranges.orange75,
  orange5: oranges.orange50,
};

const yellows = /** @type {const} */ ({
  yellow50: '#FFF6CC',
  yellow75: '#FFE999',
  yellow100: '#FFD54C',
  yellow200: '#FFBA00',
  yellow300: '#E09900',
  yellow400: '#CC7A00',
  yellow500: '#AD6200',
  yellow600: '#8A4900',
  yellow700: '#663000',
  yellow800: '#522500',
  yellow900: '#421C00',
});

const legacyYellows = /** @type {const} */ ({
  yellow1: yellows.yellow300,
  yellow2: yellows.yellow200,
  yellow3: yellows.yellow100,
  yellow4: yellows.yellow75,
  yellow5: yellows.yellow50,
});

const greens = /** @type {const} */ ({
  green50: '#ECF9F1',
  green75: '#D9F2E4',
  green100: '#ABE7C5',
  green200: '#6ED498',
  green300: '#26C06E',
  green400: '#12A154',
  green500: '#108446',
  green600: '#0F6638',
  green700: '#0D4A29',
  green800: '#0E3A22',
  green900: '#082B18',
});

const legacyGreens = /** @type {const} */ ({
  green1: greens.green500,
  green2: greens.green400,
  green3: greens.green300,
  green4: greens.green75,
  green5: greens.green50,
});

const blues = /** @type {const} */ ({
  blue50: '#F2F6FC',
  blue75: '#E4ECFB',
  blue100: '#CADBF7',
  blue200: '#A3C0F5',
  blue300: '#7FA7F0',
  blue400: '#548AF0',
  blue500: '#306BD9',
  blue600: '#1952BA',
  blue700: '#113B8A',
  blue800: '#0B2F70',
  blue900: '#0B234F',
});

const legacyBlues = /** @type {const} */ ({
  blue1: blues.blue500,
  blue2: blues.blue400,
  blue3: blues.blue300,
  blue4: blues.blue75,
  blue5: blues.blue50,
  darkblue1: blues.blue600,
  darkblue2: blues.blue600,
  darkblue3: blues.blue400,
  darkblue4: blues.blue75,
});

const semantics = {
  /**
   * Main body text color
   */
  text: grays.gray900,
  /**
   * De-emphasized / footer text color
   */
  textLighter: grays.gray400,
  /**
   * Regular hyperlinks color
   */
  link: blues.blue500,
  /**
   * Regular hyperlinks hover color
   */
  linkHover: blues.blue400,
  /**
   * Regular hyperlinks active color
   */
  linkActive: blues.blue600,
  /**
   * Color for borders
   */
  border: grays.gray200,
  /**
   * Color for lines, visual separators
   */
  line: grays.gray75,
  /**
   * Body background color
   */
  background: grays.gray50,
  /**
   * A background color for highlighting text
   */
  highlight: yellows.yellow75,
  /**
   * A faint color for backgrounds, borders, and accents that do not require high contrast with the background color
   */
  muted: grays.gray200,
  /**
   * Primary brand color for links, buttons, etc.
   */
  primary: oranges.orange300,
  /**
   * Primary brand color for links, buttons, etc.
   */
  primaryDark: oranges.orange400,
  /**
   * Primary brand color for links, buttons, etc.
   */
  primaryLight: oranges.orange200,
  /**
   * Background color. Primary brand color for links, buttons, etc.
   */
  primaryBG: oranges.orange75,
  /**
   * Alternative lighter background color to primaryBG.
   */
  primaryBGLight: oranges.orange50,
  /**
   * A secondary brand color for alternative styling
   */
  secondary: greens.green400,
  /**
   * A secondary brand color for alternative styling
   */
  secondaryDark: greens.green500,
  /**
   * A secondary brand color for alternative styling
   */
  secondaryLight: greens.green300,
  /**
   * Background color. A secondary brand color for alternative styling
   */
  secondaryBG: greens.green75,
  /**
   * Alternative lighter background color to secondaryBG.
   */
  secondaryBGLight: greens.green50,
  /**
   * A contrast color for emphasizing UI
   */
  accent: oranges.orange300,
  /**
   * A contrast color for emphasizing UI
   */
  accentDark: oranges.orange400,
  /**
   * A contrast color for emphasizing UI
   */
  accentLight: oranges.orange200,
  /**
   * Background color. A contrast color for emphasizing UI
   */
  accentBG: oranges.orange75,
  /**
   * Alternative lighter background color to accentBG.
   */
  accentBGLight: oranges.orange50,
  /**
   * Subtle information or details historic in nature. Often already actioned & in the past. No priority required due to the lack of importance or change.
   */
  neutral: grays.gray200,
  /**
   * Subtle information or details historic in nature. Often already actioned & in the past. No priority required due to the lack of importance or change.
   */
  neutralDark: grays.gray400,
  /**
   * Subtle information or details historic in nature. Often already actioned & in the past. No priority required due to the lack of importance or change.
   */
  neutralLight: grays.gray75,
  /**
   * Background color. Subtle information or details historic in nature. Often already actioned & in the past. No priority required due to the lack of importance or change.
   */
  neutralBG: grays.gray50,
  /**
   * Alternative lighter background color to neutralBG.
   */
  neutralBGLight: grays.gray50,
  /**
   * Super important, high urgency, high risk, errors...
   */
  critical: reds.red500,
  /**
   * Super important, high urgency, high risk, errors...
   */
  criticalDark: reds.red400,
  /**
   * Super important, high urgency, high risk, errors...
   */
  criticalLight: reds.red600,
  /**
   * Background color. Super important, high urgency, high risk, errors...
   */
  criticalBG: reds.red75,
  /**
   * Alternative lighter background color to criticalBG.
   */
  criticalBGLight: reds.red50,
  /**
   * Guidance & advice. Generally from the UI or business. Functional, calm and non urgent.
   */
  info: blues.blue400,
  /**
   * Guidance & advice. Generally from the UI or business. Functional, calm and non urgent.
   */
  infoDark: blues.blue500,
  /**
   * Guidance & advice. Generally from the UI or business. Functional, calm and non urgent.
   */
  infoLight: blues.blue300,
  /**
   * Background color. Guidance & advice. Generally from the UI or business. Functional, calm and non urgent.
   */
  infoBG: blues.blue75,
  /**
   * Alternative lighter background  to infoBG.
   */
  infoBGLight: blues.blue50,
  /**
   * Good vibes & new things! Wants to get the user to act on something or be aware something good happened.
   */
  positive: greens.green400,
  /**
   * Good vibes & new things! Wants to get the user to act on something or be aware something good happened.
   */
  positiveDark: greens.green500,
  /**
   * Good vibes & new things! Wants to get the user to act on something or be aware something good happened.
   */
  positiveLight: greens.green300,
  /**
   * Background color. Good vibes & new things! Wants to get the user to act on something or be aware something good happened.
   */
  positiveBG: greens.green75,
  /**
   * Alternative lighter background color to positiveBG.
   */
  positiveBGLight: greens.green50,
  /**
   * You should be aware something is happening or mid-way through a process. Could be a risk or an item that needs to be acted upon.
   */
  caution: yellows.yellow200,
  /**
   * You should be aware something is happening or mid-way through a process. Could be a risk or an item that needs to be acted upon.
   */
  cautionDark: yellows.yellow300,
  /**
   * You should be aware something is happening or mid-way through a process. Could be a risk or an item that needs to be acted upon.
   */
  cautionLight: yellows.yellow100,
  /**
   * Background color. You should be aware something is happening or mid-way through a process. Could be a risk or an item that needs to be acted upon.
   */
  cautionBG: yellows.yellow75,
  /**
   * Alternative lighter background color to cautionBG.
   */
  cautionBGLight: yellows.yellow50,
  /**
   * Negative
   */
  negative: reds.red500,
  /**
   * Notice
   */
  notice: yellows.red200,
};

export const alias = {
  /**
   * Brand color for decorations: banner, illustrations, tag...
   */
  backgroundBrand: oranges.orange300,
  /**
   * Highlight background for decorations; Highlighted announcer
   */
  backgroundBrandLight: oranges.orange50,

  /**
   * Info Snackbar
   */
  backgroundInfo: blues.blue500,
  /**
   * Info Announcer
   */
  backgroundInfoLight: blues.blue50,
  /**
   * Success Snackbar
   */
  backgroundSuccess: greens.green400,
  /**
   * Success / Payment Announcer
   */
  backgroundSuccessLight: greens.green50,
  /**
   * Critical error Snackbar
   */
  backgroundError: reds.red500,
  /**
   * Critical error Announcer
   */
  backgroundErrorLight: reds.red50,
  /**
   * Warning Snackbar
   */
  backgroundWarning: oranges.orange300,
  /**
   * Warning Announcer
   */
  backgroundWarningLight: oranges.orange50,
  /**
   * Appwrapper header, customized for verticals
   */
  backgroundAppwrapper: yellows.yellow200,
  /**
   * Layer 1 (background) or layer 2 (container)
   */
  backgroundPrimary: legacyGrays.white,
  /**
   * Background for default snackbar
   */
  backgroundInverted: grays.gray900,
  /**
   * Layer 1 (background) or layer 2 (container). Often used for neutral announcer
   */
  backgroundSecondary: grays.gray50,
  /**
   * Overlay layer: 50% black for popup/drawer
   */
  backgroundOverlay: grays.gray900 + '80',
  /**
   * Layer 3 over secondary background
   */
  backgroundTertiary: grays.gray75,

  /**
   * Chotot's original Yellow, used for banner or illustrations
   */
  backgroundChotot: yellows.yellow200,

  /**
   * Brand text color
   */
  textBrand: oranges.orange300,
  /**
   * Primary body copy; Headers, text button
   */
  textPrimary: grays.gray900,
  /**
   * Secondary copy / Help text, often used in conjunction with bold text-primary
   */
  textSecondary: grays.gray600,
  /**
   * Tertiary copy; Help text
   */
  textTertiary: grays.gray400,
  /**
   * Link button color
   */
  textLink: blues.blue500,
  /**
   * Information messages. Used for small text with no interaction
   */
  textInfo: blues.blue500,
  /**
   * Success message. Used for small text with no interaction
   */
  textSuccess: greens.green400,
  /**
   * Error message
   */
  textError: reds.red500,
  /**
   * Text color for critical messages. Used for small text with no interaction
   */
  textWarning: oranges.orange400,
  /**
   * Disabled text color
   */
  textDisabled: grays.gray200,
  /**
   * Text on color background
   */
  textBlank: legacyGrays.white,
  /**
   * Primary button solid background color
   */
  buttonPrimary: oranges.orange300,
  /**
   * Primary info button background color
   */
  buttonInfo: blues.blue500,
  /**
   * Primary success button background color
   */
  buttonSuccess: greens.green400,
  /**
   * Primary danger button background color
   */
  buttonError: reds.red500,
  /**
   * Chotot brand button background color
   */
  buttonChotot: yellows.yellow100,
  /**
   * Tertiary button border and text color
   */
  buttonBlank: legacyGrays.white,
  /**
   * Disabled state color for all buttons
   */
  buttonDisabled: grays.gray200,
  /**
   * Primary button solid background color
   */
  buttonAppwrapper: oranges.orange300,
  /**
   * Secondary button tonal background color
   */
  buttonSecondary: oranges.orange50,
  /**
   * Secondary info button background color
   */
  buttonTonalInfo: blues.blue50,
  /**
   * Secondary success button border and text color
   */
  buttonTonalSuccess: greens.green50,
  /**
   * Secondary danger button background color
   */
  buttonTonalError: reds.red50,
  /**
   * Secondary neutral button background color
   */
  buttonTonalNeutral: grays.gray50,
  /**
   * Used for divider or card stroke
   */
  borderThin: grays.gray75,
  /**
   * Border for inputs and buttons
   */
  borderRegular: grays.gray200,
  /**
   * Hover border
   */
  borderBold: grays.gray400,
  /**
   * Focus or selected state of inputs
   */
  borderActive: oranges.orange300,
  /**
   * White border for badge / labels
   */
  borderBlank: legacyGrays.white,
  /**
   * Border customized by verticals
   */
  borderBrand: oranges.orange300,
  /**
   * Info state color for borders text
   */
  borderInfo: blues.blue500,
  /**
   * Success state color for borders text
   */
  borderSuccess: greens.green400,
  /**
   * Error state color for borders text
   */
  borderError: reds.red500,
  /**
   * Disabled state color for borders
   */
  borderDisabled: grays.gray200,
  /**
   * Backup color, currently not in used
   */
  borderBlack: grays.gray900,
  /**
   * Brand color for icons
   */
  iconBrand: oranges.orange300,
  /**
   * Primary icons, often used with primary text
   */
  iconPrimary: grays.gray900,
  /**
   * Secondary icons, often used with secondary text
   */
  iconSecondary: grays.gray600,
  /**
   * Tertiary icons, often used with tertiary text
   */
  iconTertiary: grays.gray400,
  /**
   * Icons on interactive colors
   */
  iconBlank: legacyGrays.white,
  /**
   * Icons on interactive colors
   */
  iconInfo: blues.blue500,
  /**
   * Icons on interactive colors
   */
  iconSuccess: greens.green400,

  /**
   * Icons on interactive colors
   */
  iconError: reds.red500,
  /**
   * Icons for warning case
   */
  iconWarning: oranges.orange300,
  /**
   * Icons on interactive colors
   */
  iconChotot: yellows.yellow200,
  /**
   * Disabled state for icons
   */
  iconDisabled: grays.gray200,

  /**
   * Hover color for “button-secondary”
   */
  interactionBrandHover: oranges.orange200,
  /**
   * Active color for “button-button-secondary”
   */
  interactionBrandPressed: oranges.orange400,
  /**
   * Active color for “button-button-secondary”
   */
  interactionBrandLightHover: oranges.orange75,
  /**
   * Active color for “button-button-secondary”
   */
  interactionBrandLightPressed: oranges.orange100,
  /**
   * Information
   */
  interactionInfoHover: blues.blue400,
  /**
   * Information
   */
  interactionInfoPressed: blues.blue600,
  /**
   * Subtle background color for “support-info”
   */
  interactionInfoLightHover: blues.blue75,
  /**
   * Subtle background color for “support-info”
   */
  interactionInfoLightPressed: blues.blue100,
  /**
   * Success
   */
  interactionSuccessHover: greens.green300,
  /**
   * Success
   */
  interactionSuccessPressed: greens.green500,
  /**
   * Success
   */
  interactionSuccessLightHover: greens.green75,
  /**
   * Success
   */
  interactionSuccessLightPressed: greens.green100,
  /**
   * Error
   */
  interactionErrorHover: reds.red400,
  /**
   * Error
   */
  interactionErrorPressed: reds.red600,
  /**
   * Subtle background color for “support-error”
   */
  interactionErrorLightHover: reds.red75,
  /**
   * Subtle background color for “support-error”
   */
  interactionErrorLightPressed: reds.red100,
  /**
   * Hover color for “button-tertiary”
   */
  interactionNeutralHover: grays.gray600,
  /**
   * Pressed color for “button-tertiary”
   */
  interactionNeutralPressed: grays.gray700,
  /**
   * Hover color for “button-tertiary”
   */
  interactionNeutralLightHover: grays.gray75,
  /**
   * Active color for “button-tertiary”
   */
  interactionNeutralLightPressed: grays.gray100,
  /**
   * For more flexibility in design
   */
  interactionChototHover: yellows.yellow75,
  /**
   * For more flexibility in design
   */
  interactionChototPressed: yellows.yellow200,
  /**
   * Hover color for elements on white background
   */
  interactionPrimaryHover: grays.gray50,
};

/**
 * theme.colors
 *
 * color, background-color, border-color
 */
const colors = {
  ...legacyGrays,
  ...legacyReds,
  ...legacyOranges,
  ...legacyYellows,
  ...legacyGreens,
  ...legacyBlues,
  ...blues,
  ...oranges,
  ...greens,
  ...reds,
  ...grays,
  ...yellows,
  ...semantics,
  ...alias,
};

/**
 * @type {{
  '0': '0px',
  '1': '1px',
  '2': '2px',
  '3': '3px',
  '4': '4px',
}}
 */
const bw = {
  0: '0px',
  1: '1px',
  2: '2px',
  3: '3px',
  4: '4px',
};

/**
 * theme.borderWidths
 *
 * border-width
 */
const borderWidths = {
  ...bw,
  none: bw[0],
  sm: bw[1],
  md: bw[2],
  lg: bw[3],
  xl: bw[4],
};

/**
 * theme.borderStyles
 *
 * border-style
 */
const borderStyles = {
  none: 'none',
  solid: 'solid',
};

/**
 * theme.borders
 *
 * border, border-top, border-right, border-bottom, border-left
 */
const borders = {
  none: '0',
  sm: `${borderWidths.sm} ${borderStyles.solid} ${colors.line}`,
};

/**
 * @type {[
  '0',
  '2px',
  '4px',
  '8px',
  '12px',
  '20px',
]}
 */
// Radius Scale update step from 14px to 12px
// Since Design System Update (16/5/2022)
const radiusScales = [
  '0', // 0
  '2px', // 1
  '4px', // 2
  '8px', // 3
  '12px', // 4
  '20px', // 5
];
/**
 * theme.radii
 *
 * border-radius
 */
const radii = {
  // ...radiusScales,
  /**
   * No border radius
   */
  none: radiusScales[0],
  /**
   * Used for images, thumbnails
   */
  xs: radiusScales[1],
  /**
   * Used for buttons
   */
  sm: radiusScales[2],
  /**
   * Unused
   */
  md: radiusScales[3],
  /**
   * Unused
   */
  lg: radiusScales[4],
  /**
   * Used for c2c home page thumbnails
   */
  xl: radiusScales[5],
  /**
   * Used for pill buttons
   *
   * '9999px'
   */
  pill: '9999px',
  /**
   * Used for icons frames
   *
   * '100%'
   */
  circle: '100%',
};

/**
 * theme.shadows
 *
 * box-shadow, text-shadow
 * @type {{
  none: 'none',
  sm: '0px 2px 4px rgba(0,0,0,.5)',
  md: '0px 0px 8px rgba(0,0,0,.3)',
  lg: '2px 2px 14px rgba(0,0,0,.3)',
}}
 */
const shadows = {
  none: 'none',
  sm: '0px 2px 4px rgba(0,0,0,.5)',
  md: '0px 0px 8px rgba(0,0,0,.3)',
  lg: '2px 2px 14px rgba(0,0,0,.3)',
};

/**
 * theme.letterSpacings
 *
 * letter-spacing
 */
const letterSpacings = [];

/**
 * theme.zIndices
 *
 * z-index
 * @type {{
  initial: 'auto',
  neutral: 0,
  up: 1,
  down: -1,
  notification: 1900,
  tooltip: 1800,
  modal: 1600,
  dropdown: 1700,
}}
 */
const zIndices = {
  initial: 'auto',
  neutral: 0,
  up: 1,
  down: -1,
  notification: 1900,
  tooltip: 1800,
  modal: 1600,
  dropdown: 1700,
};

/**
 * theme.gradients
 *
 * background-image
 *
 * @beta This key is NOT standard styled-system's theme specs (hence not applicable for `sx()`)
 * @type {{
  toDark: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 3%, #101426 100%)',
  toLight: 'linear-gradient(0deg, #FFFFFF 0%, rgba(255, 255, 255, 0.00) 100%)',
}}
 */
const gradients = {
  toDark: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 3%, #101426 100%)',
  toLight: 'linear-gradient(0deg, #FFFFFF 0%, rgba(255, 255, 255, 0.00) 100%)',
};

/**
 * theme.durations
 *
 * transition-duration, animation-duration, (js) setTimeout, setInterval
 *
 * Unit-less value in ms
 *
 * @beta This key is NOT standard styled-system's theme specs (hence not applicable for `sx()`).
 *
 * @type {{
  none: 0,
  xxxs: 100,
  xxs: 150,
  xs: 300,
  sm: 500,
  md: 1000,
  lg: 2000,
  xl: 4000,
  xxl: 6000,
  xxxl: 8000,
  xxxxl: 10000,
}}
 */
const durations = {
  none: 0,
  xxxs: 100,
  xxs: 150,
  xs: 300,
  sm: 500,
  md: 1000,
  lg: 2000,
  xl: 4000,
  xxl: 6000,
  xxxl: 8000,
  xxxxl: 10000,
};

/**
 * The theme object
 *
 * Keys are sorted in alphabetically order
 */
const theme = Object.freeze({
  borders,
  borderStyles,
  borderWidths,
  breakpoints,
  colors,
  durations,
  fonts,
  fontSizes,
  fontWeights,
  gradients,
  letterSpacings,
  lineHeights,
  mediaQueries,
  radii,
  shadows,
  sizes,
  space,
  zIndices,
});

export default theme;
